import { LoginPageLayout } from '@h2hspark/auth-ui';
import * as React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { useMemberAppInstallability } from '../pwa/use-member-app-installability';
import { JOIN_LABEL } from '../welcome-constants';

/**
 * Member sign-in — `Web App.dc.html` §3.2, the LOGIN screen.
 *
 * ## What changed on 2026-09-12, and why it was the last screen to change
 *
 * This screen had never been built from the prototype at all. It was the shared layout's default
 * chrome: a grey 48px tile with an **H** in it above two stacked headings, the fields at 36px on
 * white, and Google offered last, under the password, as the third of four text links. The
 * August parity report named the monogram and the September one named it again — by then it was
 * the only surface left in the member journey that did not look like the product, because
 * everything either side of it had been restyled around it.
 *
 * ## Element by element against §3.2 (l.1298–1316)
 *
 * | The prototype | Here |
 * | --- | --- |
 * | Ivory full-height page | `AuthPageShell`, `bg-background` |
 * | White 64px bar: `assets/logotype.png` at 20px → home, "Back to home" → home | `topBar` below. The asset is `/brand/logotype.png`, which is that file byte for byte |
 * | `assets/icon.png` at 56px, centred | `/brand/icon.png`, added to `public/brand/` by this change — it was the one mark of the four the repository did not hold |
 * | H1 "Welcome back" | `title` |
 * | "Log in to see your matches and follow-ups." | `passwordDescription` |
 * | Card: white, `--radius-xl`, `--shadow-sm`, 28px | the `brand` chrome's card |
 * | "Continue with Google", 52px outlined, four-colour glyph | `GoogleWordButton`, rendered by `SocialSignInButtons` so the real flow is behind it |
 * | Divider "or" | the `brand` chrome's rule |
 * | Email, then Password with "Forgot?" on its label row | `LoginForm` under `forgotPasswordPlacement="under-field"` — **"Forgot?" is under the box, not on the label row** (TW 44164956) |
 * | "Log in", full-width brand pill | the `brand` chrome's submit |
 * | "New to H2HSpark? Get started" under the card | `signupPrompt` + `signupLabel` |
 *
 * **Two deliberate differences, and the second arrived on 2026-09-23.** *"Forgot?"* keeps §3.2's
 * word and loses §3.2's position: it is drawn **under the password box** rather than on its label
 * row, because on the label row it sits above the box and a keyboard reaches it between Email and
 * Password — TW 44164956, approved by Sumit, recorded in `USR-001`'s Notes. The organizer and
 * admin sign-ins move with it: all three take `chrome="brand"`, which is the one place the
 * placement is decided.
 *
 * **The first is the last row, and it is `MEM-046` AC-6 rather than a
 * preference.** The prototype's link is called *"Get started"*; ours is called **"Join H2HSpark"**,
 * which is what the front door and the registration wizard's own final button say. Three names
 * for one door is what AC-6 exists to prevent, and the prototype is inconsistent with itself here
 * — its "Get started" goes straight to Discover without registering anybody (inventory §3.2,
 * *"'Get started' also skips any sign-up"*), so there is no behaviour to preserve underneath the
 * word.
 *
 * Everything the screen *does* is unchanged: Google, the MFA challenge, recovery, the sanitised
 * `?next=`, the `/app/auth/callback` path, and installability.
 */
export function MemberLoginPage(): React.ReactElement {
  useMemberAppInstallability();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Auth-internal routes are stripped from `?next=` so a redirect chain can't land the
  // member back on, say, `/app/auth/callback` with an exchange code that has been spent.
  const requestedNext = searchParams.get('next') ?? '/app';
  const nextPath = /^\/app\/(login|join|recovery|mfa|auth)\b/.test(requestedNext)
    ? '/app'
    : requestedNext;
  const inboundError = searchParams.get('error');

  return (
    <LoginPageLayout
      chrome="brand"
      /*
        §3.2's bar. "Back to home" is the marketing site at `/`, which is where the prototype's
        `goHome` goes — the member app and the marketing site share this origin, so it is a real
        destination and not the self-link the organizer footer has.

        Both are `<Link>`s rather than buttons because both navigate: a member can open either in
        a new tab, and a screen reader announces them for what they are.
      */
      topBar={
        <>
          <Link to="/" className="rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring">
            <img src="/brand/logotype.png" alt="H2HSpark" className="h-5 w-auto" />
          </Link>
          <Link
            to="/"
            className="rounded-md font-brand text-sm font-bold text-muted-foreground hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring"
          >
            Back to home
          </Link>
        </>
      }
      headerInitial="H"
      /*
        `assets/icon.png` at 56px — the mark itself rather than a letter in a box. `alt=""`
        because the bar above already carries the product's name in its own `alt`, and a second
        announcement of "H2HSpark" immediately before the heading "Welcome back" is noise.
      */
      //
      // A link home as well, since TW 44132158 (Sumit, 2026-09-18): the mark at the top of a
      // sign-in screen is where people reach to leave it, and the bar's logotype is out of sight
      // on a phone once the form is scrolled. Named for where it goes, because the image itself
      // is `alt=""`.
      headerLogo={
        <Link
          to="/"
          aria-label="H2HSpark home"
          className="inline-block rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring"
        >
          <img src="/brand/icon.png" alt="" className="mx-auto h-14 w-auto" />
        </Link>
      }
      title="Welcome back"
      passwordDescription="Log in to see your matches and follow-ups."
      inboundError={inboundError}
      nextPath={nextPath}
      onSuccess={() => navigate(nextPath, { replace: true })}
      onMfaRequired={(challenge) =>
        navigate('/app/mfa-challenge', { state: { challenge, nextPath }, replace: true })
      }
      onForgotPassword={() => navigate('/app/recovery')}
      // The member app shares an origin with the marketing site and mounts everything under
      // `/app`, so its `<SocialCallback>` is at `/app/auth/callback` and not at the shared
      // default. Without this the IdP returned to a route this app does not have, the catch-all
      // sent the member to the marketing home page, and the code exchange never ran — the
      // Google half of `USR-001` AC-1 looked implemented and did not work.
      socialCallbackPath="/app/auth/callback"
      // `MEM-046` AC-6 — the same words as the front door and the wizard's last button, so a
      // member who arrives here first is offered the same door by the same name.
      signupPrompt="New to H2HSpark?"
      signupLabel={JOIN_LABEL}
      onSignup={() => navigate('/app/join')}
    />
  );
}
